import React, { useState, useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import CookieConsent from "react-cookie-consent";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";

import "./App.css";
import "./styles.css";
import "./styles/ui-kit.css";

import Header from "./layouts/Header/Header";
import AppRoutes from "./routes/AppRoutes";
import { useAppAuth } from "./hooks/useAppAuth";
import GoogleOneTap from "./components/GoogleOneTap";
import TermsUpdateModal from "./components/TermsUpdateModal";
import { useNavigate } from "react-router-dom";
import { initNativeNotifications } from "./services/nativeNotificationService";

const DeepLinkInitializer = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return;

    const handleAppUrlOpen = (event) => {
      try {
        const parsedUrl = new URL(event.url);
        const path = parsedUrl.pathname + parsedUrl.search;
        navigate(path);
      } catch (err) {
        console.error("Erro ao processar deep link:", err);
      }
    };

    CapacitorApp.addListener("appUrlOpen", handleAppUrlOpen);

    return () => {
      CapacitorApp.removeAllListeners();
    };
  }, [navigate]);

  return null;
};

const HardwareBackButtonInitializer = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return;

    const handleBackButton = async (event) => {
      const path = window.location.pathname;
      if (path === "/" || path === "/welcome") {
        CapacitorApp.exitApp();
      } else {
        navigate(-1);
      }
    };

    const backButtonListener = CapacitorApp.addListener("backButton", handleBackButton);

    return () => {
      backButtonListener.then((l) => l.remove());
    };
  }, [navigate]);

  return null;
};

const NativeNotificationInitializer = ({ userId }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;
    let unsub;
    initNativeNotifications(userId, (path) => {
      navigate(path);
    }).then((unsubFn) => {
      unsub = unsubFn;
    });

    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [userId, navigate]);

  return null;
};

const publicRoutes = [
  "/welcome",
  "/privacy-policy",
  "/contact-us",
  "/about-us",
  "/tree-profile",
  "/userprofile",
  "/article",
  "/blog",
];

const App = () => {
  const authProps = useAppAuth();
  const { user, isLoading, isUserDataLoading } = authProps;
  
  const [allowAds, setAllowAds] = useState(() => {
    return Capacitor.getPlatform() === 'android' || localStorage.getItem('allowAds') === 'true';
  });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem("theme") === "dark";
  });

  // Ensure main CSS is loaded
  useEffect(() => {
    document.body.classList.add('app-loaded');
  }, []);

  // Update HTML class when dark mode changes
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDarkMode);
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  // Listener for system theme change
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = (event) => {
      if (!localStorage.getItem("theme")) {
        setIsDarkMode(event.matches);
      }
    };
    mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, []);

  // Listener for custom themeChange events from user profile config
  useEffect(() => {
    const handleThemeChange = (e) => {
      setIsDarkMode(e.detail === "dark");
    };
    window.addEventListener("themeChanged", handleThemeChange);
    return () => {
      window.removeEventListener("themeChanged", handleThemeChange);
    };
  }, []);

  // Automatic redirect if a non-logged user attempts to visit protected route
  useEffect(() => {
    if (isLoading) return;

    const path = window.location.pathname;
    const isPublic = publicRoutes.some(route => path.startsWith(route));

    if (!user && !isPublic) {
      window.location.href = "/welcome";
    }
  }, [user, isLoading]);

  // Sincronização em background para cadastros offline acumulados (PRO)
  useEffect(() => {
    if (user) {
      import("./services/offlineSyncService").then((m) => {
        m.default.syncPending();
      });
    }
  }, [user]);

  return (
    <Router basename="/">
      <DeepLinkInitializer />
      <HardwareBackButtonInitializer />
      <NativeNotificationInitializer userId={user?.uid} />
      <div id="App">
        {!user && !isLoading && <GoogleOneTap />}
        {Capacitor.getPlatform() !== 'android' && (

          <CookieConsent
            location="bottom"
            buttonText="Aceito"
            declineButtonText="Recusar"
            enableDeclineButton
            onAccept={() => {
              localStorage.setItem('allowAds', 'true');
              setAllowAds(true);
            }}
            onDecline={() => {
              localStorage.setItem('allowAds', 'false');
              setAllowAds(false);
            }}
          >
            Utilizamos cookies para personalizar anúncios e melhorar sua experiência no site.
          </CookieConsent>
        )}

        {!isLoading && (
          <Header authProps={authProps} />
        )}

        <div id="page-wrap">
          <AppRoutes authProps={authProps} />
        </div>
        <TermsUpdateModal authProps={authProps} />
      </div>
    </Router>
  );
};

export default App;